import { useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Flag, MessagesSquare } from 'lucide-react';
import usePageTitle from '../../../core/hooks/UsePageTitle';
import DefaultLayout from '../../layouts/default/Default.layout';
import AccessDenied from '../../components/AccessDenied/AccessDenied';
import ModerationQueue from '../../components/Forum/ModerationQueue';
import ReportsQueue from '../../components/Forum/ReportsQueue';
import useNotifications from '../../../core/hooks/useNotifications';

/**
 * `/admin/moderacao` — a casca da moderação do fórum.
 *
 * Uma rota só com abas (decisão 3 do design): o frame `UaS7p` do Pencil trata
 * Fórum e Denúncias como uma tela com duas abas, não duas telas. A guarda de
 * perfil é a da rota (`gated(ADMIN, …)` em `MyRoutes.tsx`) — quem não é
 * administrador/master nem monta esta view, então nenhuma chamada admin sai.
 *
 * ⚠️ A entrada de menu correspondente é **manual** em `Sidebar.tsx`: o menu não
 * lê a tabela de rotas. Ao mexer numa, confira a outra.
 *
 * Os badges das abas vêm de `moderation`, o mesmo `GET /moderation/stats`
 * que o sino e o Sidebar já leem via `useNotifications()` — esta view não faz
 * a sua própria chamada. Fazê-la seria uma **segunda** requisição para o
 * mesmo dado: o hook já busca uma vez por instância montada, e a instância
 * desta view é só mais uma leitora, não uma fonte à parte. A aba inativa não
 * monta a sua fila e ainda assim precisa do número — é por isso que o número
 * vem de fora dela. O mesmo objeto desce para a `ModerationQueue`, que usa os
 * KPIs do dia.
 *
 * Aprovar/rejeitar comentários e decidir denúncias chama
 * `applyModerationDelta`, que decrementa `pendingComments`/`openReports` no
 * `useModerationCountersStore` compartilhado — a mesma leitura que alimenta o
 * sino e o Sidebar, então os dois badges caem junto com o da aba, sem
 * navegação e sem um round-trip extra por ação.
 */

type ModerationTab = 'forum' | 'reports';

const TABS: { key: ModerationTab; label: string; icon: typeof MessagesSquare }[] = [
  { key: 'forum', label: 'Fórum', icon: MessagesSquare },
  { key: 'reports', label: 'Denúncias', icon: Flag },
];

/** `?aba=denuncias` mantém a aba na URL, para link direto e recarregamento. */
const TAB_PARAM: Record<ModerationTab, string> = { forum: 'forum', reports: 'denuncias' };

function parseTab(value: string | null): ModerationTab {
  return value === TAB_PARAM.reports ? 'reports' : 'forum';
}

const cx = (...v: Array<string | false | null | undefined>) => v.filter(Boolean).join(' ');

export default function ModeracaoView() {
  usePageTitle('Moderação');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const active = parseTab(searchParams.get('aba'));
  const { moderation, moderationDenial, applyModerationDelta } = useNotifications();

  /** Troca de aba é só estado na URL — nada recarrega a página. */
  const selectTab = useCallback(
    (tab: ModerationTab) => {
      setSearchParams(tab === 'forum' ? {} : { aba: TAB_PARAM[tab] }, { replace: true });
    },
    [setSearchParams],
  );

  const countFor = (tab: ModerationTab) =>
    tab === 'forum' ? moderation?.pendingComments : moderation?.openReports;

  /** Comentários aprovados/rejeitados em `ModerationQueue` — decrementa `pendingComments`. */
  const handlePendingResolved = useCallback(
    (count: number) => applyModerationDelta({ pendingComments: count }),
    [applyModerationDelta],
  );

  /** Uma denúncia decidida (corrigida/rejeitada/desativada) em `ReportsQueue` — decrementa `openReports`. */
  const handleReportResolved = useCallback(
    (count: number) => applyModerationDelta({ openReports: count }),
    [applyModerationDelta],
  );

  return (
    <DefaultLayout hideFilter>
      <div className="flex flex-col gap-5 p-6">
        <header className="flex flex-col gap-4">
          <div>
            <p className="text-[11px] font-semibold tracking-wider text-ink-secondary">
              ADMINISTRAÇÃO
            </p>
            <h1 className="text-2xl font-bold tracking-tight text-ink">Moderação</h1>
          </div>

          <div role="tablist" aria-label="Moderação" className="flex gap-6 border-b border-edge">
            {TABS.map(({ key, label, icon: Icon }) => {
              const selected = active === key;
              const count = countFor(key);
              return (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  id={`moderacao-tab-${key}`}
                  aria-selected={selected}
                  aria-controls={`moderacao-panel-${key}`}
                  onClick={() => selectTab(key)}
                  className={cx(
                    'flex items-center gap-2 border-b-2 px-0.5 pb-3 text-sm font-semibold',
                    'transition-colors duration-150 motion-reduce:transition-none',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-select/60',
                    selected
                      ? 'border-select text-ink'
                      : 'border-transparent text-ink-secondary hover:text-ink',
                  )}
                >
                  <Icon size={16} strokeWidth={1.75} aria-hidden />
                  {label}
                  {count !== undefined && (
                    <span
                      data-testid={`tab-badge-${key}`}
                      className={cx(
                        'rounded-full px-[7px] py-px text-[11px] font-bold',
                        selected
                          ? 'bg-brand text-brand-contrast'
                          : 'bg-surface-muted text-ink-secondary',
                      )}
                    >
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </header>

        {moderationDenial ? (
          <AccessDenied
            kind={moderationDenial.kind!}
            detail={moderationDenial.raw}
            variant="panel"
            onNavigate={navigate}
          />
        ) : (
          TABS.map(({ key }) => (
            <div
              key={key}
              role="tabpanel"
              id={`moderacao-panel-${key}`}
              aria-labelledby={`moderacao-tab-${key}`}
              hidden={active !== key}
            >
              {/* A fila só monta na aba ativa: a inativa não deve gastar as
                  suas três chamadas de sub-fila até o admin abri-la. */}
              {active === key &&
                (key === 'forum' ? (
                  <ModerationQueue stats={moderation} onPendingResolved={handlePendingResolved} />
                ) : (
                  <ReportsQueue onReportResolved={handleReportResolved} />
                ))}
            </div>
          ))
        )}
      </div>
    </DefaultLayout>
  );
}
